import { nomePessoa, textoLivre } from '../../nucleo/formato';

/** Fase 2, W15: tipos do chat da equipe (espelham os DTOs de `modules/equipe/src/consultas.ts`). */
export type TipoCanal = 'departamento' | 'grupo' | 'direta' | 'registro';
export type Visibilidade = 'publico' | 'privado';
export type Papel = 'dono' | 'membro';

export interface UltimaMensagem {
  id: string;
  autor_id: string | null;
  autor_nome: string | null;
  tipo: 'texto' | 'sistema';
  texto: string;
  parametros: ParametrosSistema | null;
  apagada: boolean;
  anexos: number;
  created_at: string;
}

export interface Canal {
  id: string;
  tipo: TipoCanal;
  nome: string | null;
  descricao: string | null;
  visibilidade: Visibilidade;
  departamento_id: string | null;
  vinculo_entidade: string | null;
  vinculo_id: string | null;
  vinculo_link: string | null;
  arquivado: boolean;
  participa: boolean;
  papel: Papel | null;
  membros: number;
  nao_lidas: number;
  outro: { id: string; nome: string } | null;
  ultima: UltimaMensagem | null;
  ultima_mensagem_em: string | null;
  created_at: string;
}

export interface Participante {
  usuario_id: string;
  nome: string;
  papel: Papel;
}

export interface CanalDetalhe extends Canal {
  participantes: Participante[];
  lido_ate: string | null;
  pode_gerir: boolean;
  pode_escrever: boolean;
}

export interface Anexo {
  id: string;
  nome: string;
  mime: string;
  tamanho: number;
  familia: 'imagem' | 'audio' | 'video' | 'documento';
}

export interface ParametrosSistema {
  modelo?: string;
  ator?: string;
  alvos?: string[];
  nome?: string;
}

export interface Mensagem {
  id: string;
  canal_id: string;
  autor_id: string | null;
  autor_nome: string | null;
  tipo: 'texto' | 'sistema';
  texto: string;
  parametros: ParametrosSistema | null;
  origem: 'tela' | 'agente' | 'chave' | 'sistema';
  mencoes: { id: string; nome: string }[];
  anexos: Anexo[];
  resposta: { id: string; autor_nome: string | null; texto: string; apagada: boolean; anexos: number } | null;
  editada_em: string | null;
  apagada: boolean;
  created_at: string;
}

export interface ResultadoBusca {
  id: string;
  canal_id: string;
  canal_tipo: TipoCanal;
  canal_nome: string | null;
  outro_nome: string | null;
  autor_id: string | null;
  autor_nome: string | null;
  texto: string;
  created_at: string;
}

export interface Pagina<T> {
  itens: T[];
  proximo_cursor: string | null;
}

export interface Pessoa {
  id: string;
  nome: string;
}

/** Iniciais para o avatar (só as iniciais: também no modo discreto, W20). */
export function iniciais(nome: string | null | undefined): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  return ((partes[0]![0] ?? '') + (partes.length > 1 ? (partes[partes.length - 1]![0] ?? '') : '')).toUpperCase();
}

/** Nome do canal (dado cru): a conversa direta mostra a outra pessoa. Na tela, use `nomeDoCanalNaTela`. */
export function nomeDoCanal(c: Pick<Canal, 'tipo' | 'nome' | 'outro'>): string {
  if (c.tipo === 'direta') return c.outro?.nome ?? '';
  return c.nome ?? '';
}

/**
 * Fase 2 (W20): nome do canal na tela, pelo modo discreto. A conversa direta mostra a outra pessoa por `nomePessoa` (iniciais
 * com a opção de nomes); grupo, departamento e discussão são texto livre (`textoLivre`, com os nomes das pessoas do chat).
 */
export function nomeDoCanalNaTela(c: Pick<Canal, 'tipo' | 'nome' | 'outro'>, nomes: readonly (string | null | undefined)[] = []): string {
  if (c.tipo === 'direta') return nomePessoa(c.outro?.nome);
  return textoLivre(c.nome ?? '', ...nomes);
}

export const urlAnexo = (canalId: string, id: string) => `/api/v1/equipe/canais/${canalId}/anexos/${id}`;

/** Tamanho legível (B, KB, MB) no idioma. */
export function tamanhoArquivo(bytes: number, idioma: string): string {
  const fmt = (v: number) => new Intl.NumberFormat(idioma === 'pt' ? 'pt-BR' : idioma, { maximumFractionDigits: 1 }).format(v);
  if (bytes < 1024) return `${fmt(bytes)} B`;
  if (bytes < 1024 * 1024) return `${fmt(bytes / 1024)} KB`;
  return `${fmt(bytes / 1024 / 1024)} MB`;
}

/** Pedaço do texto da mensagem: texto, link (só http e https) ou menção de quem foi citado. */
export type Pedaco = { tipo: 'texto'; valor: string } | { tipo: 'link'; valor: string; href: string } | { tipo: 'mencao'; valor: string };

const RE_LINK = /\bhttps?:\/\/[^\s<>"']+/gi;

/**
 * Quebra o texto em pedaços para o React mostrar escapado: link vira <a> com o endereço como texto (sem pontuação final
 * grudada) e "@Nome" de quem foi mencionado vira destaque. Nada vira HTML. Só http e https viram link (javascript:,
 * data: e afins ficam como texto).
 */
export function pedacosDoTexto(texto: string, mencionados: readonly string[] = []): Pedaco[] {
  const saida: Pedaco[] = [];
  const nomes = [...new Set(mencionados.filter(Boolean))].sort((a, b) => b.length - a.length);
  const empurrarTexto = (t: string) => {
    if (!t) return;
    if (!nomes.length) return void saida.push({ tipo: 'texto', valor: t });
    let resto = t;
    while (resto) {
      let melhor: { i: number; nome: string } | null = null;
      for (const n of nomes) {
        const i = resto.indexOf(`@${n}`);
        if (i >= 0 && (!melhor || i < melhor.i)) melhor = { i, nome: n };
      }
      if (!melhor) {
        saida.push({ tipo: 'texto', valor: resto });
        break;
      }
      if (melhor.i > 0) saida.push({ tipo: 'texto', valor: resto.slice(0, melhor.i) });
      saida.push({ tipo: 'mencao', valor: `@${melhor.nome}` });
      resto = resto.slice(melhor.i + melhor.nome.length + 1);
    }
  };
  let ultimo = 0;
  for (const m of texto.matchAll(RE_LINK)) {
    let url = m[0];
    // Pontuação no fim (ponto, vírgula, parêntese de fechamento) fica fora do link.
    const sobra = /[.,;:!?)\]]+$/.exec(url)?.[0] ?? '';
    if (sobra) url = url.slice(0, -sobra.length);
    const inicio = m.index!;
    empurrarTexto(texto.slice(ultimo, inicio));
    let valido = false;
    try {
      const u = new URL(url);
      valido = u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      valido = false;
    }
    if (valido) saida.push({ tipo: 'link', valor: url, href: url });
    else empurrarTexto(url);
    ultimo = inicio + url.length;
  }
  empurrarTexto(texto.slice(ultimo));
  return saida;
}
